import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, CreditCard, Phone } from 'lucide-react';
import { VerificationResultPanel } from '@/components/verification/VerificationResultPanel';
import { PinVerificationModal } from '@/components/user/PinVerificationModal';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Loader } from '@/components/ui/Loader';
import { ROUTES } from '@/constants/routes';
import { useDashboardUi } from '@/components/layout/UserLayout';
import { useUserAuth } from '@/context/UserAuthContext';
import { verificationService, getApiErrorMessage } from '@/services';
import type {
  BvnSlipType,
  BvnVerificationMethod,
  VerificationPricingRow,
  VerificationResult,
} from '@/types/verification';
import { generateBVNSlipPDF } from '@/utils/verification/bvnSlipPdfGenerator';
import { cn } from '@/utils/cn';
import { notify } from '@/utils/notify';

function formatNaira(amount: number) {
  return `₦${amount.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const SLIP_OPTIONS: { id: BvnSlipType; title: string; description: string }[] = [
  { id: 'basic_slip', title: 'Basic Verification', description: 'Essential identity details' },
  { id: 'advanced_slip', title: 'Advanced Verification', description: 'Comprehensive verification details' },
];

export function BvnVerificationPage() {
  const { user, persistUser } = useUserAuth();
  const { loadWallet } = useDashboardUi();

  const [loading, setLoading] = useState(true);
  const [configured, setConfigured] = useState(true);
  const [pricing, setPricing] = useState<VerificationPricingRow[]>([]);
  const [verificationMethod, setVerificationMethod] = useState<BvnVerificationMethod>('by_bvn_id');
  const [selectedSlip, setSelectedSlip] = useState<BvnSlipType | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [showPinModal, setShowPinModal] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [result, setResult] = useState<VerificationResult | null>(null);

  const loadPricing = useCallback(async () => {
    setLoading(true);
    try {
      const res = await verificationService.getPricing();
      setConfigured(res.data?.configured ?? true);
      setPricing(res.data?.pricing.bvn ?? []);
    } catch (error) {
      notify.error(getApiErrorMessage(error, 'Failed to load pricing'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPricing();
  }, [loadPricing]);

  useEffect(() => {
    setInputValue('');
    setSelectedSlip(null);
  }, [verificationMethod]);

  const getSlipPrice = useMemo(
    () => (slipType: BvnSlipType) =>
      pricing.find((p) => p.slipType === slipType && p.verificationMethod === verificationMethod)?.price,
    [pricing, verificationMethod],
  );

  const selectedPrice = selectedSlip ? getSlipPrice(selectedSlip) : undefined;
  const canSubmit = Boolean(selectedSlip && inputValue.trim().length >= 10 && selectedPrice != null);

  async function handleVerify(pin: string) {
    if (!selectedSlip) return;

    setVerifying(true);
    try {
      const res = await verificationService.verifyBvn({
        verificationMethod,
        verificationType: selectedSlip,
        value: inputValue.trim(),
        pin,
      });
      const payload = res.data;

      if (!payload?.success) {
        notify.error(payload?.message || 'Verification could not be completed');
        return;
      }

      setResult(payload);
      if (payload.user) persistUser(payload.user);
      await loadWallet();
      notify.success(payload.message || 'BVN verified successfully');
      setShowPinModal(false);
    } catch (error) {
      notify.error(getApiErrorMessage(error, 'Verification failed'));
    } finally {
      setVerifying(false);
    }
  }

  async function handleDownloadPdf() {
    if (!result?.data || !result.slipType) return;
    setDownloading(true);
    try {
      await generateBVNSlipPDF(
        {
          bvn: result.data.bvn || '',
          firstName: result.data.firstName || '',
          middleName: result.data.middleName || '',
          lastName: result.data.lastName || '',
          dateOfBirth: result.data.dateOfBirth,
          phoneNumber1: result.data.phoneNumber1,
          gender: result.data.gender,
          image: result.data.image,
          lgaOfOrigin: result.data.lgaOfOrigin,
          lgaOfResidence: result.data.lgaOfResidence,
          nameOnCard: result.data.nameOnCard,
          phoneNumber2: result.data.phoneNumber2,
          registrationDate: result.data.registrationDate,
          residentialAddress: result.data.residentialAddress,
          stateOfOrigin: result.data.stateOfOrigin,
          stateOfResidence: result.data.stateOfResidence,
          enrollmentBank: result.data.enrollmentBank,
          enrollmentBranch: result.data.enrollmentBranch,
          levelOfAccount: result.data.levelOfAccount,
          maritalStatus: result.data.maritalStatus,
          nin: result.data.nin,
          title: result.data.title,
          watchListed: result.data.watchListed,
        },
        result.slipType as 'basic_slip' | 'advanced_slip',
        result.dateGenerated,
      );
    } catch (error) {
      notify.error('Failed to generate PDF');
      console.error(error);
    } finally {
      setDownloading(false);
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <Loader label="Loading verification options..." />
      </div>
    );
  }

  if (result?.success) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Link
            to={ROUTES.dashboardServices}
            className="inline-flex rounded-lg border border-gray-200 p-2 text-gray-600 hover:bg-gray-50"
          >
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-brand-navy">BVN Verification</h1>
            <p className="text-sm text-brand-muted">Download your slip or verify another record.</p>
          </div>
        </div>
        <VerificationResultPanel result={result} onDownload={handleDownloadPdf} downloading={downloading} />
        <Button type="button" variant="outline" onClick={() => setResult(null)}>
          Verify another
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3">
        <Link
          to={ROUTES.dashboardServices}
          className="mt-1 inline-flex rounded-lg border border-gray-200 p-2 text-gray-600 hover:bg-gray-50"
        >
          <ArrowLeft className="h-4 w-4" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-brand-navy">BVN Verification</h1>
          <p className="text-sm text-brand-muted">
            Verify by 11-digit BVN or the phone number linked to the BVN.
          </p>
        </div>
      </div>

      {!configured && (
        <Card className="border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          Identity verification is temporarily unavailable. Please try again later.
        </Card>
      )}

      <Card className="mx-auto max-w-xl space-y-6 p-6">
        <div className="grid grid-cols-2 gap-2 rounded-xl bg-gray-100 p-1">
          <button
            type="button"
            onClick={() => setVerificationMethod('by_bvn_id')}
            className={cn(
              'rounded-lg px-3 py-2 text-sm font-medium transition',
              verificationMethod === 'by_bvn_id' ? 'bg-white shadow text-brand-navy' : 'text-gray-600',
            )}
          >
            By BVN
          </button>
          <button
            type="button"
            onClick={() => setVerificationMethod('by_phone')}
            className={cn(
              'rounded-lg px-3 py-2 text-sm font-medium transition',
              verificationMethod === 'by_phone' ? 'bg-white shadow text-brand-navy' : 'text-gray-600',
            )}
          >
            By Phone
          </button>
        </div>

        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700">
            {verificationMethod === 'by_bvn_id' ? 'BVN Number' : 'Phone Number'}
          </label>
          <div className="relative">
            {verificationMethod === 'by_bvn_id' ? (
              <CreditCard className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            ) : (
              <Phone className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            )}
            <input
              className="w-full rounded-xl border border-gray-300 py-2.5 pl-10 pr-3 text-sm"
              placeholder={verificationMethod === 'by_bvn_id' ? 'Enter 11-digit BVN' : '08123456789'}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
            />
          </div>
        </div>

        <div className="space-y-3">
          <p className="text-sm font-medium text-gray-700">Verification type</p>
          <div className="grid gap-3 sm:grid-cols-2">
            {SLIP_OPTIONS.map((option) => {
              const price = getSlipPrice(option.id);
              const selected = selectedSlip === option.id;
              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setSelectedSlip(option.id)}
                  className={cn(
                    'rounded-2xl border p-4 text-left transition',
                    selected
                      ? 'border-brand-green bg-emerald-50 ring-2 ring-brand-green/30'
                      : 'border-gray-200 hover:border-brand-green/40',
                  )}
                >
                  <p className="font-semibold text-brand-navy">{option.title}</p>
                  <p className="mt-1 text-xs text-gray-500">{option.description}</p>
                  <p className="mt-3 text-lg font-bold text-brand-green">
                    {price != null ? formatNaira(price) : '—'}
                  </p>
                </button>
              );
            })}
          </div>
        </div>

        <div className="rounded-xl bg-gray-50 px-4 py-3 text-sm text-gray-500">
          Wallet balance: ₦{(user?.walletBalance ?? 0).toLocaleString()}
        </div>

        <Button type="button" fullWidth disabled={!canSubmit || !configured} onClick={() => setShowPinModal(true)}>
          Verify & Pay
        </Button>
      </Card>

      <PinVerificationModal
        open={showPinModal}
        onClose={() => setShowPinModal(false)}
        onConfirm={handleVerify}
        loading={verifying}
        title="Confirm BVN verification"
        description={`You will be charged ${selectedPrice != null ? formatNaira(selectedPrice) : ''} from your wallet.`}
      />
    </div>
  );
}
